import React from 'react';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import {
  TextField,
  Select,
  MenuItem,
  Box,
  FormHelperText,
  Typography,
  Checkbox,
  FormControlLabel,
  Button,
  Tooltip,
  LinearProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useParams } from 'react-router';
import { usePackagerUpdate } from '../../hooks/packager/usePackagerUpdate';
import {
  DeliveryScheduleTypes,
  DeliveryDataGroupingStrategiesTypes,
  DeliveryMode,
  NamingConfig,
  ParentRelationship,
} from '../../../types/packager-types';
import { packagerFormDirtyAtom } from '../../../atoms/packager-atoms';
import { useAtom } from 'jotai';
import DialogContentGlynt from '../../common/Dialog/DialogContentGlynt';
import DialogActionsGlynt from '../../common/Dialog/DialogActionsGlynt';
import { FormControlStyled, FormLabelStyled } from '../../common/Dialog/DialogGlynt';
import useGetPackager from '../../hooks/packager/useGetPackager';
import useGetPackagerAttributes from '../../hooks/packager/useGetPackagerAttributes';
import { formatEnumToLabel } from '../../../utils/formatEnumToLabel';
import PackagerConfigUnsavedChanges from './PackagerConfigUnsavedChanges';
import PackagerCronSuggestions from './PackagerCronSuggestions';
import NamingElementsList from './components/NamingElementsList';
import { ALL_NAMING_ELEMENT_TYPES, generateFormatPreview } from './constants';
import { featureFlags } from '../../../utils/featureFlags';
import cronstrue from 'cronstrue';
import { describeCronSchedule } from '../../../utils/cronNextRun';

const DEFAULT_NAMING: NamingConfig = { elements: [], separator: '_' };

interface PackagerStep2DeliveryProps {
  updatePackagerMutation: ReturnType<typeof usePackagerUpdate>;
  handleCloseConfirmation: () => void;
  handlePrevious: () => void;
  handleNext: (skipGuard?: boolean) => void;
  simulateMode?: boolean;
  draftChanges?: Partial<import('../../../types/packager-types').Packager>;
  setDraftChanges?: (changes: Partial<import('../../../types/packager-types').Packager>) => void;
  triggerSimulation?: () => void;
}

interface FormData {
  delivery_data_grouping_strategy: DeliveryDataGroupingStrategiesTypes;
  delivery_data_grouping_metadata_key: string;
  delivery_schedule_type: DeliveryScheduleTypes;
  delivery_cron_expression: string;
  output_directory: string;
  include_source_files_in_delivery: boolean;
  include_parent_files_in_delivery: boolean;
  include_duplicates_in_delivery: boolean;
  delivery_mode: DeliveryMode;
  source_file_naming_config: NamingConfig;
  parent_source_file_rel_types: ParentRelationship[];
}

const PARENT_REL_TYPE_OPTIONS: { value: ParentRelationship; label: string }[] = [
  { value: ParentRelationship.DUPLICATE, label: 'Duplicates' },
  { value: ParentRelationship.SPLIT, label: 'Splits' },
  { value: ParentRelationship.TRANSLATED, label: 'Translations' },
  { value: ParentRelationship.REINTRODUCED, label: 'Re-introduced' },
];

const formatRelTypeLabel = (value: ParentRelationship): string =>
  PARENT_REL_TYPE_OPTIONS.find((o) => o.value === value)?.label ?? value;

const DELIVERY_DATA_GROUPING_STRATEGY_VALUES = Object.values(DeliveryDataGroupingStrategiesTypes);
const DELIVERY_SCHEDULE_TYPE_VALUES = Object.values(DeliveryScheduleTypes);

const DELIVERY_MODE_OPTIONS = [
  { value: DeliveryMode.INDIVIDUAL, label: 'Individual files only (default)' },
  { value: DeliveryMode.ZIP_ONLY, label: 'ZIP archive only' },
  { value: DeliveryMode.BOTH, label: 'Individual files + ZIP archive' },
];

const slugify = (input: string): string =>
  input
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const PackagerStep2Delivery: React.FC<PackagerStep2DeliveryProps> = ({
  updatePackagerMutation,
  handleCloseConfirmation,
  handlePrevious,
  handleNext,
  simulateMode = false,
  draftChanges = {},
  setDraftChanges,
  triggerSimulation,
}) => {
  const { dpid } = useParams<{ dpid: string }>();
  const { packager, isLoading } = useGetPackager(dpid!);
  const [showUnsavedDialog, setShowUnsavedDialog] = React.useState(false);
  const [pendingCloseAction, setPendingCloseAction] = React.useState<(() => void) | null>(null);
  const [showCronSuggestions, setShowCronSuggestions] = React.useState(false);
  const [formDirty, setFormDirty] = useAtom(packagerFormDirtyAtom);

  const {
    control,
    handleSubmit,
    formState: { errors, isValid, isDirty },
    watch,
    setValue,
    trigger,
  } = useForm<FormData>({
    defaultValues: {
      delivery_data_grouping_strategy:
        packager?.delivery_data_grouping_strategy || DeliveryDataGroupingStrategiesTypes.ALL_IN_ONE,
      delivery_data_grouping_metadata_key: packager?.delivery_data_grouping_metadata_key || '',
      delivery_schedule_type: packager?.delivery_schedule_type || DeliveryScheduleTypes.MANUAL,
      delivery_cron_expression: packager?.delivery_cron_expression || '',
      output_directory: packager?.output_directory || (packager?.label ? slugify(packager.label) : ''),
      include_source_files_in_delivery: packager?.include_source_files_in_delivery || false,
      include_parent_files_in_delivery: packager?.include_parent_files_in_delivery || false,
      include_duplicates_in_delivery: packager?.include_duplicates_in_delivery || false,
      delivery_mode: packager?.delivery_mode || DeliveryMode.INDIVIDUAL,
      source_file_naming_config: packager?.source_file_naming_config || DEFAULT_NAMING,
      parent_source_file_rel_types: packager?.parent_source_file_rel_types || [],
    },
    mode: 'onChange',
  });

  const { data: metaDataOptions, isLoading: isLoadingMetadata } = useGetPackagerAttributes(
    dpid!,
    packager?.id || ''
  );

  const METADATA_OPTIONS = React.useMemo(() => {
    const attrs = metaDataOptions?.attributes;
    if (!attrs) return [];
    const options: { value: string; label: string }[] = [];
    attrs.data_pool?.forEach((o: string) => options.push({ value: o, label: o }));
    attrs.extraction_batch?.forEach((o: string) => options.push({ value: o, label: o }));
    attrs.document?.forEach((o: string) => options.push({ value: o, label: o }));
    attrs.organization?.forEach((o: string) => options.push({ value: o, label: o }));
    return options;
  }, [metaDataOptions]);

  // DOCUMENT_DATA naming options come from the curated, validator-backed subset
  // the API exposes (available-attributes.document_data_fields), not a hardcoded list.
  const DOCUMENT_DATA_OPTIONS = React.useMemo(
    () => (metaDataOptions?.document_data_fields || []).map((f: string) => ({ value: f, label: f })),
    [metaDataOptions]
  );

  const NAMING_ELEMENT_TYPES = React.useMemo(() => {
    if (!packager) return ALL_NAMING_ELEMENT_TYPES;
    if (packager.delivery_data_grouping_strategy !== 'SPLIT_BY_DOC') {
      return ALL_NAMING_ELEMENT_TYPES.filter((t) => t.value !== 'DOCUMENT_DATA');
    }
    return ALL_NAMING_ELEMENT_TYPES;
  }, [packager]);

  const {
    fields: namingFields,
    append: namingAppend,
    remove: namingRemove,
    replace: namingReplace,
    move: namingMove,
  } = useFieldArray({ control, name: 'source_file_naming_config.elements' });

  const watchedNamingConfig = watch('source_file_naming_config');

  React.useEffect(() => {
    if (!packager) return;
    setValue(
      'delivery_data_grouping_strategy',
      packager.delivery_data_grouping_strategy || DeliveryDataGroupingStrategiesTypes.ALL_IN_ONE
    );
    setValue(
      'delivery_data_grouping_metadata_key',
      packager.delivery_data_grouping_metadata_key || ''
    );
    setValue(
      'delivery_schedule_type',
      packager.delivery_schedule_type || DeliveryScheduleTypes.MANUAL
    );
    setValue('delivery_cron_expression', packager.delivery_cron_expression || '');
    setValue(
      'output_directory',
      packager.output_directory || (packager.label ? slugify(packager.label) : '')
    );
    setValue(
      'include_source_files_in_delivery',
      packager.include_source_files_in_delivery || false
    );
    setValue(
      'include_parent_files_in_delivery',
      packager.include_parent_files_in_delivery || false
    );
    setValue('include_duplicates_in_delivery', packager.include_duplicates_in_delivery || false);
    setValue('delivery_mode', packager.delivery_mode || DeliveryMode.INDIVIDUAL);
    setValue('parent_source_file_rel_types', packager.parent_source_file_rel_types || []);
    const naming = packager.source_file_naming_config || DEFAULT_NAMING;
    setValue('source_file_naming_config', naming);
    namingReplace(naming.elements || []);
  }, [packager, setValue, namingReplace]);

  React.useEffect(() => {
    if (!simulateMode) return;
    if (draftChanges.delivery_data_grouping_strategy !== undefined)
      setValue('delivery_data_grouping_strategy', draftChanges.delivery_data_grouping_strategy);
    if (draftChanges.delivery_data_grouping_metadata_key !== undefined)
      setValue(
        'delivery_data_grouping_metadata_key',
        (draftChanges.delivery_data_grouping_metadata_key as string) || ''
      );
    if (draftChanges.delivery_schedule_type !== undefined)
      setValue('delivery_schedule_type', draftChanges.delivery_schedule_type);
    if (draftChanges.delivery_cron_expression !== undefined)
      setValue('delivery_cron_expression', (draftChanges.delivery_cron_expression as string) || '');
    if (draftChanges.output_directory !== undefined)
      setValue('output_directory', (draftChanges.output_directory as string) || '');
    if (draftChanges.include_source_files_in_delivery !== undefined)
      setValue(
        'include_source_files_in_delivery',
        Boolean(draftChanges.include_source_files_in_delivery)
      );
    if (draftChanges.include_parent_files_in_delivery !== undefined)
      setValue(
        'include_parent_files_in_delivery',
        Boolean(draftChanges.include_parent_files_in_delivery)
      );
    if (draftChanges.include_duplicates_in_delivery !== undefined)
      setValue(
        'include_duplicates_in_delivery',
        Boolean(draftChanges.include_duplicates_in_delivery)
      );
    if (draftChanges.delivery_mode !== undefined)
      setValue('delivery_mode', draftChanges.delivery_mode);
    if (draftChanges.parent_source_file_rel_types !== undefined)
      setValue(
        'parent_source_file_rel_types',
        (draftChanges.parent_source_file_rel_types as ParentRelationship[]) || []
      );
    if (draftChanges.source_file_naming_config !== undefined) {
      setValue('source_file_naming_config', draftChanges.source_file_naming_config);
      namingReplace(draftChanges.source_file_naming_config.elements || []);
    }
  }, [simulateMode, draftChanges, setValue, namingReplace]);

  React.useEffect(() => {
    setFormDirty(isDirty);
  }, [isDirty, setFormDirty]);

  const onSubmit = (data: FormData) => {
    if (!packager?.id) return;

    const cleanedNaming: NamingConfig = {
      ...data.source_file_naming_config,
      elements: (data.source_file_naming_config.elements || []).map((element) => {
        const base = { type: element.type, value: element.value };
        if (
          element.type === 'METADATA' &&
          element.value &&
          (element.value.includes('updated_at') || element.value.includes('created_at')) &&
          element.format
        ) {
          return { ...base, format: element.format };
        }
        return base;
      }),
    };

    const payload: Partial<import('../../../types/packager-types').Packager> = {
      delivery_data_grouping_strategy: data.delivery_data_grouping_strategy,
      delivery_data_grouping_metadata_key:
        data.delivery_data_grouping_strategy ===
        DeliveryDataGroupingStrategiesTypes.METADATA_MATCH_AND_MERGE
          ? data.delivery_data_grouping_metadata_key
          : undefined,
      delivery_schedule_type: data.delivery_schedule_type,
      delivery_cron_expression: data.delivery_cron_expression,
      output_directory: data.output_directory || undefined,
      include_source_files_in_delivery: data.include_source_files_in_delivery,
      include_duplicates_in_delivery: data.include_duplicates_in_delivery,
      delivery_mode: data.delivery_mode,
    };
    if (featureFlags.includeParentFilesInDelivery) {
      payload.include_parent_files_in_delivery =
        data.include_source_files_in_delivery && data.include_parent_files_in_delivery;
    }
    if (data.include_source_files_in_delivery) {
      payload.source_file_naming_config = cleanedNaming;
      payload.parent_source_file_rel_types = data.parent_source_file_rel_types || [];
    } else {
      payload.parent_source_file_rel_types = [];
    }

    if (simulateMode && setDraftChanges) {
      setDraftChanges({ ...draftChanges, ...payload });
      setFormDirty(false);
      handleNext(true);
    } else {
      updatePackagerMutation.mutate({
        dataPoolId: dpid!,
        packagerId: packager.id,
        data: payload,
      });
    }
  };

  const handleFormSubmit = () => {
    if (isDirty) {
      handleSubmit(onSubmit)();
    } else {
      handleNext();
    }
  };

  const handleCloseWithCheck = (closeAction: () => void) => {
    if (formDirty) {
      setPendingCloseAction(() => closeAction);
      setShowUnsavedDialog(true);
    } else {
      closeAction();
    }
  };

  const handleConfirmClose = () => {
    setShowUnsavedDialog(false);
    if (pendingCloseAction) {
      pendingCloseAction();
      setPendingCloseAction(null);
    }
  };

  const handleCancelClose = () => {
    setShowUnsavedDialog(false);
    setPendingCloseAction(null);
  };

  if (isLoading || !packager) {
    return <LinearProgress />;
  }

  return (
    <>
      <DialogContentGlynt scrollable>
        <FormControlStyled fullWidth>
          <FormLabelStyled>GROUPING STRATEGY</FormLabelStyled>
          <Controller
            name="delivery_data_grouping_strategy"
            control={control}
            rules={{ required: 'Grouping strategy is required' }}
            render={({ field }) => (
              <Select
                {...field}
                variant="outlined"
                size="small"
                data-cy="delivery-data-grouping-strategy-select"
                error={!!errors.delivery_data_grouping_strategy}
              >
                {DELIVERY_DATA_GROUPING_STRATEGY_VALUES.map((value) => (
                  <MenuItem
                    key={value}
                    value={value}
                    data-cy={`delivery-data-grouping-strategy-${value.toLowerCase().replace(/_/g, '-')}`}
                  >
                    {formatEnumToLabel(value)}
                  </MenuItem>
                ))}
              </Select>
            )}
          />
          {errors.delivery_data_grouping_strategy && (
            <FormHelperText error>{errors.delivery_data_grouping_strategy.message}</FormHelperText>
          )}
        </FormControlStyled>

        {watch('delivery_data_grouping_strategy') ===
          DeliveryDataGroupingStrategiesTypes.METADATA_MATCH_AND_MERGE && (
          <FormControlStyled fullWidth>
            <FormLabelStyled>DELIVERY DATA GROUPING METADATA KEY</FormLabelStyled>
            <Controller
              name="delivery_data_grouping_metadata_key"
              control={control}
              rules={{
                required: 'Metadata key is required when using METADATA_MATCH_AND_MERGE strategy',
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  placeholder="Enter metadata key for grouping"
                  fullWidth
                  variant="outlined"
                  size="small"
                  error={!!errors.delivery_data_grouping_metadata_key}
                  helperText={errors.delivery_data_grouping_metadata_key?.message}
                  data-cy="delivery-data-grouping-metadata-key-input"
                />
              )}
            />
          </FormControlStyled>
        )}

        <FormControlStyled fullWidth>
          <FormLabelStyled>DELIVERY SCHEDULE TYPE</FormLabelStyled>
          <Controller
            name="delivery_schedule_type"
            control={control}
            rules={{ required: 'Delivery schedule type is required' }}
            render={({ field }) => (
              <Select
                {...field}
                variant="outlined"
                size="small"
                data-cy="delivery-schedule-type-select"
                error={!!errors.delivery_schedule_type}
              >
                {DELIVERY_SCHEDULE_TYPE_VALUES.map((value) => (
                  <MenuItem
                    key={value}
                    value={value}
                    data-cy={`delivery-schedule-type-${value.toLowerCase()}`}
                  >
                    {formatEnumToLabel(value)}
                  </MenuItem>
                ))}
              </Select>
            )}
          />
          {errors.delivery_schedule_type && (
            <FormHelperText error>{errors.delivery_schedule_type.message}</FormHelperText>
          )}
        </FormControlStyled>

        {watch('delivery_schedule_type') === DeliveryScheduleTypes.CRON_SCHEDULE && (
          <FormControlStyled fullWidth>
            <FormLabelStyled>DELIVERY CRON EXPRESSION</FormLabelStyled>
            <Typography variant="body2" sx={{ marginBottom: '8px', color: 'text.secondary' }}>
              Enter a cron expression to schedule when the delivery should occur. Times are in UTC.
            </Typography>

            <Box
              sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
            >
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Need help with cron expressions?
              </Typography>
              <Box
                component="button"
                type="button"
                onClick={() => setShowCronSuggestions(!showCronSuggestions)}
                sx={{
                  background: 'none',
                  border: '1px solid #1976d2',
                  borderRadius: 1,
                  px: 2,
                  py: 0.5,
                  cursor: 'pointer',
                  fontSize: '0.75rem',
                  color: '#1976d2',
                  '&:hover': { backgroundColor: '#f3f8ff' },
                }}
              >
                {showCronSuggestions ? 'Hide Suggestions' : 'Show Suggestions'}
              </Box>
            </Box>

            {showCronSuggestions && (
              <PackagerCronSuggestions
                onSelectExpression={(expr) => {
                  setValue('delivery_cron_expression', expr);
                  trigger('delivery_cron_expression');
                }}
              />
            )}

            <Controller
              name="delivery_cron_expression"
              control={control}
              rules={{
                required: 'Delivery cron expression is required when using CRON_SCHEDULE',
                validate: (value) => {
                  if (!value) return 'Delivery cron expression is required';
                  const parts = value.trim().split(/\s+/);
                  if (parts.length !== 5) {
                    return 'Cron expression must have exactly 5 parts: minutes hours day-of-month month day-of-week';
                  }
                  try {
                    cronstrue.toString(value);
                    return true;
                  } catch (error) {
                    return 'Invalid cron expression. Please check your syntax.';
                  }
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  placeholder="0 17 * * *"
                  fullWidth
                  variant="outlined"
                  size="small"
                  error={!!errors.delivery_cron_expression}
                  helperText={errors.delivery_cron_expression?.message}
                  data-cy="delivery-cron-expression-input"
                />
              )}
            />

            {watch('delivery_cron_expression') && !errors.delivery_cron_expression && (
              <FormHelperText sx={{ color: 'success.main', mt: 1 }}>
                {(() => {
                  try {
                    return describeCronSchedule(watch('delivery_cron_expression'));
                  } catch (error) {
                    return 'Invalid cron expression';
                  }
                })()}
              </FormHelperText>
            )}
          </FormControlStyled>
        )}

        <FormControlStyled fullWidth>
          <FormLabelStyled>DELIVERY MODE</FormLabelStyled>
          <Typography variant="body2" sx={{ marginBottom: '8px', color: 'text.secondary' }}>
            Choose how files are delivered in the package output.
          </Typography>
          <Controller
            name="delivery_mode"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                select
                variant="outlined"
                size="small"
                data-cy="delivery-mode-input"
              >
                {DELIVERY_MODE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </FormControlStyled>

        <FormControlStyled fullWidth sx={{ '&.MuiFormControl-root': { mt: 1 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Controller
              name="include_source_files_in_delivery"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      {...field}
                      checked={field.value}
                      size="small"
                      data-cy="include-source-files-checkbox"
                    />
                  }
                  label="Include source files"
                />
              )}
            />
            <Tooltip
              title="Include the original document files (e.g. PDFs) in the package."
              placement="top"
              arrow
            >
              <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
            </Tooltip>
          </Box>

          {featureFlags.includeParentFilesInDelivery &&
            watch('include_source_files_in_delivery') && (
              <Box sx={{ display: 'flex', alignItems: 'center', ml: 3.5 }}>
                <Controller
                  name="include_parent_files_in_delivery"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          {...field}
                          checked={field.value}
                          size="small"
                          data-cy="include-parent-files-checkbox"
                        />
                      }
                      label="Include parent files"
                    />
                  )}
                />
                <Tooltip
                  title="Also include parent files for split, duplicate, and translated documents so customers can verify those were handled correctly."
                  placement="top"
                  arrow
                >
                  <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                </Tooltip>
              </Box>
            )}

          {watch('include_source_files_in_delivery') && (
            <Box sx={{ mt: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                <FormLabelStyled sx={{ mb: 0 }}>
                  ALSO SHIP SOURCE FILES FOR EXCLUDED LINEAGE PARENTS
                </FormLabelStyled>
                <Tooltip
                  title="For each selected relationship, ship the excluded lineage parent's source file (e.g. the original PDF of a duplicate) alongside the package. These parents will also appear DELIVERED in the CoC report. This is independent of 'Include duplicates in delivery', which controls duplicate data rows."
                  placement="top"
                  arrow
                >
                  <InfoOutlinedIcon
                    sx={{ ml: 0.5, fontSize: 16, color: 'text.secondary', cursor: 'help' }}
                  />
                </Tooltip>
              </Box>
              <Controller
                name="parent_source_file_rel_types"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    value={field.value || []}
                    onChange={(e) => {
                      const v = e.target.value;
                      field.onChange(Array.isArray(v) ? v : [v]);
                    }}
                    select
                    fullWidth
                    size="small"
                    variant="outlined"
                    data-cy="parent-source-file-rel-types-input"
                    SelectProps={{
                      multiple: true,
                      displayEmpty: true,
                      renderValue: (selected) => {
                        const arr = (selected as ParentRelationship[]) || [];
                        if (arr.length === 0) {
                          return (
                            <Typography
                              component="span"
                              sx={{ color: 'text.disabled', fontSize: 13 }}
                            >
                              None — no lineage parent source files shipped
                            </Typography>
                          );
                        }
                        return arr.map(formatRelTypeLabel).join(', ');
                      },
                    }}
                  >
                    {PARENT_REL_TYPE_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        <Checkbox
                          size="small"
                          checked={(field.value || []).includes(opt.value)}
                        />
                        {opt.label}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Box>
          )}

          {watch('include_source_files_in_delivery') && (
            <Box
              sx={{
                mt: 1.5,
                p: 1.5,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                backgroundColor: 'grey.50',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 1,
                }}
              >
                <FormLabelStyled sx={{ mb: 0 }}>SOURCE FILE NAMING</FormLabelStyled>
                <Button
                  startIcon={<AddIcon />}
                  onClick={() => namingAppend({ type: 'STRING_LITERAL', value: '' })}
                  variant="outlined"
                  size="small"
                  data-cy="add-source-file-naming-element"
                  sx={{
                    fontSize: 12,
                    px: 1.5,
                    py: 0.5,
                    minHeight: 28,
                    height: 28,
                    borderRadius: 2,
                  }}
                >
                  Add Element
                </Button>
              </Box>
              <NamingElementsList
                fields={namingFields}
                control={control}
                errors={errors}
                configPath="source_file_naming_config"
                watchedConfig={watchedNamingConfig}
                metadataOptions={METADATA_OPTIONS}
                documentDataOptions={DOCUMENT_DATA_OPTIONS}
                onRemove={namingRemove}
                onMove={namingMove}
                isLoadingMetadata={isLoadingMetadata}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1 }}>
                <Controller
                  name="source_file_naming_config.separator"
                  control={control}
                  rules={{ required: 'Separator is required' }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      variant="outlined"
                      size="small"
                      sx={{ fontSize: 13, height: 32, minHeight: 32 }}
                      data-cy="source-file-naming-separator-input"
                    >
                      <MenuItem value="_">_</MenuItem>
                      <MenuItem value="-">-</MenuItem>
                      <MenuItem value=".">.</MenuItem>
                    </Select>
                  )}
                />
                <Box
                  sx={{
                    flex: 1,
                    p: 1,
                    px: 1.4,
                    borderRadius: 1,
                    backgroundColor: 'grey.200',
                    fontFamily: 'monospace',
                    fontSize: 12,
                    color: 'text.primary',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  data-cy="source-file-naming-preview"
                >
                  {(() => {
                    const elems = watchedNamingConfig?.elements || [];
                    if (elems.length === 0) return 'No elements configured';
                    return elems
                      .map((element) => {
                        switch (element.type) {
                          case 'STRING_LITERAL':
                            return element.value || '[String Literal]';
                          case 'METADATA': {
                            const v = element.value || 'Metadata';
                            if (
                              element.format &&
                              (v.includes('updated_at') || v.includes('created_at'))
                            ) {
                              return `[${v} (${generateFormatPreview(element.format)})]`;
                            }
                            return `[${v}]`;
                          }
                          case 'DOCUMENT_DATA':
                            return '[Document Data]';
                          default:
                            return '[Unknown]';
                        }
                      })
                      .join(watchedNamingConfig.separator || '_');
                  })()}
                </Box>
              </Box>
            </Box>
          )}
        </FormControlStyled>

        <FormControlStyled fullWidth sx={{ '&.MuiFormControl-root': { mt: 0 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Controller
              name="include_duplicates_in_delivery"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      {...field}
                      checked={field.value}
                      size="small"
                      data-cy="include-duplicates-checkbox"
                    />
                  }
                  label="Include duplicates"
                />
              )}
            />
            <Tooltip
              title="Include documents identified as duplicates in the delivery. Off by default."
              placement="top"
              arrow
            >
              <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
            </Tooltip>
          </Box>
        </FormControlStyled>

        <FormControlStyled fullWidth>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
            <FormLabelStyled sx={{ mb: 0 }}>OUTPUT DIRECTORY</FormLabelStyled>
            <Tooltip
              title="Auto-populated from the DataPool label. You can override."
              placement="top"
              arrow
            >
              <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
            </Tooltip>
          </Box>
          <Controller
            name="output_directory"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                placeholder="output-directory"
                fullWidth
                variant="outlined"
                size="small"
                data-cy="output-directory-input"
              />
            )}
          />
        </FormControlStyled>
      </DialogContentGlynt>
      <DialogActionsGlynt
        handleSubmit={handleFormSubmit}
        closeModal={() => handleCloseWithCheck(handleCloseConfirmation)}
        cancelCypressId="btn-packager-config-step2-modal-cancel"
        submitCypressId="btn-packager-config-step2-modal-submit"
        clearCypressId="btn-packager-config-step2-modal-clear"
        clearButton={() => handleCloseWithCheck(handlePrevious)}
        clearButtonText="Previous"
        showClearButton={true}
        submitButtonText={simulateMode ? 'Next' : 'Save & Next'}
        submitLoading={simulateMode ? false : updatePackagerMutation.isPending}
        submitDisabled={!isValid || (simulateMode ? false : updatePackagerMutation.isPending)}
      />
      <PackagerConfigUnsavedChanges
        open={showUnsavedDialog}
        onClose={handleCancelClose}
        onConfirm={handleConfirmClose}
      />
    </>
  );
};

export default PackagerStep2Delivery;
